import { useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { communityTabs, categoryKeys } from "@/mocks/community";
import BestReviewSlider from "@/pages/community/components/BestReviewSlider";
import WritePostModal from "@/pages/community/components/WritePostModal";
import PostDetailModal from "@/pages/community/components/PostDetailModal";
import { motion, AnimatePresence } from "motion/react";
import { useCommunity, type CommunityPost } from "@/hooks/useCommunity";
import { useAuth } from "@/lib/auth";
import { useNavigate as _rrNav } from "react-router-dom";

const headerVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1] as const } },
};

const listContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.05 } },
};

const listItem = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
};

const badgeColor = (badge?: string) => {
  if (badge === "마스터") return "bg-amber-50 text-amber-700 border-amber-200";
  if (badge === "정수강생") return "bg-emerald-50 text-emerald-600 border-emerald-200";
  return "";
};

const categoryStyle: Record<string, string> = {
  "공지/이벤트": "bg-red-50 text-red-600 border-red-200",
  "자유수다": "bg-secondary-50 text-secondary-700 border-secondary-200",
  "물건 Q&A": "bg-accent-50 text-accent-700 border-accent-200",
  "낙찰/임장 후기": "bg-emerald-50 text-emerald-600 border-emerald-200",
};

export default function CommunityPage() {
  const { section } = useParams();
  const navigate = useNavigate();
  const activeTab = section || "all";
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedPostId, setSelectedPostId] = useState<string | null>(null);
  const [showWriteModal, setShowWriteModal] = useState(false);
  const { user } = useAuth();
  const { posts: localPosts, loading, createPost, toggleLike, bumpView } = useCommunity();

  const filteredPosts = useMemo(() => {
    let result = localPosts;

    if (activeTab !== "all") {
      const cat = categoryKeys[activeTab];
      if (cat) result = result.filter((p) => p.category === cat);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (p) =>
          p.title.toLowerCase().includes(q) ||
          p.content.toLowerCase().includes(q) ||
          p.author.toLowerCase().includes(q)
      );
    }

    return result;
  }, [activeTab, searchQuery, localPosts]);

  const bestReviews = useMemo(() => localPosts.filter((p) => p.isBest), [localPosts]);
  const showBestSlider = activeTab === "all" || activeTab === "review";
  const isQnATab = activeTab === "qna";

  const selectedPost: CommunityPost | null = selectedPostId ? localPosts.find((p) => p.id === selectedPostId) ?? null : null;

  const handleWriteSubmit = async (data: {
    title: string;
    content: string;
    category: string;
  }): Promise<{ ok: boolean; error?: string }> => {
    return createPost(data);
  };

  // 글 상세 열 때 조회수 +1
  const openPost = (id: string) => {
    setSelectedPostId(id);
    void bumpView(id);
  };

  return (
    <div className="min-h-screen bg-[#FAFAF8] font-['Noto_Sans_KR']">
      <Navbar />

      <main className="pt-24 pb-16 px-4 md:px-6">
        <div className="max-w-[1120px] mx-auto">
          {/* Page Header */}
          <motion.div variants={headerVariants} initial="hidden" animate="visible" className="mb-6">
            <div className="flex items-center gap-2 mb-2">
              <i className="ri-chat-3-line text-accent-600"></i>
              <span className="text-xs font-medium text-accent-600">커뮤니티</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950">커뮤니티</h1>
          </motion.div>

          {/* Best Review Slider */}
          {showBestSlider && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.4 }}
            >
              <BestReviewSlider
                posts={bestReviews}
                onPostClick={(id) => openPost(id)}
              />
            </motion.div>
          )}

          {/* Tab Navigation */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.4 }}
            className="flex items-center gap-1 bg-white rounded-xl border border-foreground-200 p-1 mb-6 w-fit overflow-x-auto"
          >
            {communityTabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => { navigate(`/community/${tab.key}`); setSearchQuery(""); }}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg transition-all whitespace-nowrap ${
                  activeTab === tab.key
                    ? "bg-foreground-950 text-white font-medium"
                    : "text-foreground-600 hover:text-foreground-900 hover:bg-background-100"
                }`}
              >
                <i className={`${tab.icon} text-base`}></i>
                {tab.label}
              </button>
            ))}
          </motion.div>

          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab + searchQuery}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.25 }}
            >
              {/* Q&A AI Notice */}
              {isQnATab && (
                <div className="mb-5 p-4 rounded-xl bg-accent-50 border border-accent-200 flex items-start gap-3">
                  <div className="w-9 h-9 rounded-full bg-accent-200 flex items-center justify-center shrink-0">
                    <i className="ri-robot-2-line text-lg text-accent-700"></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-accent-800 mb-0.5">AI 경매비서가 함께합니다</p>
                    <p className="text-xs text-accent-700 leading-relaxed">
                      물건 Q&A에 질문을 작성하시면 AI 경매비서가 1분 내로 1차 권리분석 답변을 달아드립니다. 부동산 경매 전문가가 추가 답변도 함께 제공합니다.
                    </p>
                  </div>
                </div>
              )}

              {/* Main Content Card */}
              <div className="bg-white rounded-2xl border border-foreground-200 p-5 md:p-7">
                {/* Top Bar: Search + Write */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-5">
                  <div className="flex items-center gap-2">
                    <h2 className="text-base font-bold text-foreground-950">
                      {communityTabs.find((t) => t.key === activeTab)?.label || "전체"}
                    </h2>
                    <span className="text-xs text-foreground-400">총 {filteredPosts.length}개</span>
                  </div>
                  <div className="flex items-center gap-2 w-full sm:w-auto">
                    <div className="relative flex-1 sm:w-56">
                      <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-400"></i>
                      <input
                        type="text"
                        placeholder="검색"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 text-sm border border-foreground-200 rounded-lg bg-white focus:outline-none focus:border-accent-400 transition-colors"
                      />
                    </div>
                    <button
                      onClick={() => user ? setShowWriteModal(true) : navigate("/login", { state: { from: "/community" } })}
                      className="shrink-0 px-4 py-2 text-sm font-medium bg-foreground-950 text-white rounded-full hover:bg-foreground-800 transition-all whitespace-nowrap"
                    >
                      <i className="ri-pencil-line mr-1.5"></i>
                      글쓰기
                    </button>
                  </div>
                </div>

                {/* Post List */}
                <motion.div variants={listContainer} initial="hidden" animate="visible" className="divide-y divide-foreground-100">
                  {filteredPosts.map((post) => (
                    <motion.button
                      key={post.id}
                      variants={listItem}
                      whileHover={{ backgroundColor: "var(--background-50)" }}
                      onClick={() => openPost(post.id)}
                      className="w-full flex items-center gap-3 py-3.5 px-2 rounded-lg transition-all text-left"
                    >
                      {/* Status icon */}
                      {post.pinned ? (
                        <span className="shrink-0 w-6 h-6 rounded-full bg-red-50 flex items-center justify-center">
                          <i className="ri-pushpin-fill text-xs text-red-400"></i>
                        </span>
                      ) : post.resolved ? (
                        <span className="shrink-0 w-6 h-6 rounded-full bg-emerald-50 flex items-center justify-center">
                          <i className="ri-check-line text-xs text-emerald-500"></i>
                        </span>
                      ) : null}

                      {/* Category badge */}
                      <span className={`shrink-0 px-2 py-0.5 text-[11px] font-medium rounded-full border ${categoryStyle[post.category]}`}>
                        {post.category}
                      </span>

                      {/* Title */}
                      <span className="flex-1 text-sm text-foreground-950 font-medium line-clamp-1">{post.title}</span>

                      {/* Author badge */}
                      {post.authorBadge && (
                        <span className={`shrink-0 px-1.5 py-0.5 text-[11px] font-medium rounded-full border hidden md:inline-block ${badgeColor(post.authorBadge)}`}>
                          {post.authorBadge}
                        </span>
                      )}

                      {/* Comments */}
                      <span className="shrink-0 flex items-center gap-1 text-xs text-accent-600 font-medium">
                        <i className="ri-chat-3-line"></i>
                        <span>{post.commentCount}</span>
                      </span>

                      {/* Likes */}
                      <span className="shrink-0 flex items-center gap-1 text-xs text-foreground-400">
                        <i className="ri-heart-3-line"></i>
                        <span>{post.likes}</span>
                      </span>

                      {/* Meta */}
                      <span className="shrink-0 text-xs text-foreground-400 whitespace-nowrap hidden sm:inline-block">{post.author}</span>
                      <span className="shrink-0 text-xs text-foreground-300 whitespace-nowrap hidden sm:inline-block">{post.createdAt}</span>
                    </motion.button>
                  ))}
                </motion.div>

                {/* Empty State */}
                {filteredPosts.length === 0 && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-20">
                    <div className="w-14 h-14 mx-auto mb-3 rounded-2xl bg-accent-50 flex items-center justify-center">
                      <i className="ri-inbox-line text-xl text-accent-400"></i>
                    </div>
                    <p className="text-sm text-foreground-500 mb-1">검색 결과가 없습니다</p>
                    <p className="text-xs text-foreground-400">다른 검색어로 시도해보세요</p>
                  </motion.div>
                )}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </main>

      <Footer />

      {/* Write Post Modal */}
      <WritePostModal
        isOpen={showWriteModal}
        onClose={() => setShowWriteModal(false)}
        onSubmit={handleWriteSubmit}
      />

      {/* Post Detail Modal */}
      <PostDetailModal
        post={selectedPost}
        isOpen={selectedPostId !== null}
        onClose={() => setSelectedPostId(null)}
        onToggleLike={toggleLike}
      />
    </div>
  );
}